<script>
  import { inertia, router, page } from '@inertiajs/svelte';
  import Icon from '@iconify/svelte';
  import AdminLayout from '../../../components/AdminLayout.svelte';
  import Card from '../../../components/ui/Card.svelte';
  import Button from '../../../components/ui/Button.svelte';
  import ConfirmDialog from '../../../components/ui/ConfirmDialog.svelte';
  import StatusBadge from '../../../components/StatusBadge.svelte';

  let { account, workspace = null } = $props();

  let errors = $derived(page.props.errors || {});
  let self = $derived(page.props.auth?.account?.id === account.id);
  let confirmingClose = $state(false);
  let adminError = $state(null);

  function setAdmin(value) {
    adminError = null;
    router.patch(`/admin/accounts/${account.id}`, { account: { is_admin: value } }, {
      onError: () => { adminError = 'Could not update admin access. Please try again.'; },
    });
  }

  function closeAccount() {
    router.delete(`/admin/accounts/${account.id}`, {
      onFinish: () => { confirmingClose = false; },
    });
  }

  function when(value) {
    return value ? new Date(value).toLocaleString() : '—';
  }
</script>

<AdminLayout title="Account">
  <a use:inertia href="/admin/accounts" class="text-sm text-secondary-500 hover:text-primary-700 flex items-center gap-1 mb-4 w-fit">
    <Icon icon="mdi:arrow-left" /> All accounts
  </a>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <Card title="Details">
      <dl class="flex flex-col gap-3 text-sm">
        <div class="flex justify-between gap-4">
          <dt class="text-secondary-500">Email</dt>
          <dd class="font-medium text-secondary-800 flex items-center gap-2">
            {account.email}
            {#if self}<span class="text-xs text-secondary-400">(you)</span>{/if}
          </dd>
        </div>
        <div class="flex justify-between gap-4">
          <dt class="text-secondary-500">Status</dt>
          <dd><StatusBadge status={account.status} /></dd>
        </div>
        <div class="flex justify-between gap-4">
          <dt class="text-secondary-500">Admin</dt>
          <dd class="font-medium text-secondary-800">{account.is_admin ? 'Yes' : 'No'}</dd>
        </div>
        <div class="flex justify-between gap-4">
          <dt class="text-secondary-500">Created</dt>
          <dd class="text-secondary-600">{when(account.created_at)}</dd>
        </div>
      </dl>
    </Card>

    <Card title="Workspace">
      {#if workspace}
        <dl class="flex flex-col gap-3 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-secondary-500">Name</dt>
            <dd class="font-medium text-secondary-800">{workspace.name}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-secondary-500">Connected accounts</dt>
            <dd class="text-secondary-600">{workspace.social_accounts}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-secondary-500">Posts</dt>
            <dd class="text-secondary-600">{workspace.posts}</dd>
          </div>
        </dl>
      {:else}
        <p class="text-secondary-500 text-sm">No workspace yet.</p>
      {/if}
    </Card>
  </div>

  <Card title="Admin access">
    {#if errors.is_admin || adminError}
      <p class="text-sm text-red-600 mb-3">{errors.is_admin || adminError}</p>
    {/if}
    <div class="flex items-center justify-between gap-4">
      <p class="text-sm text-secondary-600">
        {account.is_admin
          ? 'This account can access the admin area.'
          : 'This account cannot access the admin area.'}
      </p>
      {#if account.is_admin}
        <Button variant="secondary" onclick={() => setAdmin(false)} disabled={self}>
          <Icon icon="mdi:shield-off-outline" class="mr-1" /> Revoke admin
        </Button>
      {:else}
        <Button onclick={() => setAdmin(true)}>
          <Icon icon="mdi:shield-crown-outline" class="mr-1" /> Make admin
        </Button>
      {/if}
    </div>
  </Card>

  {#if account.status !== 'closed' && !self}
    <Card title="Danger zone">
      {#if errors.base}
        <p class="text-sm text-red-600 mb-3">{errors.base}</p>
      {/if}
      <div class="flex items-center justify-between gap-4">
        <p class="text-sm text-secondary-600">Closing revokes the account's access.</p>
        <Button variant="danger" onclick={() => { confirmingClose = true; }}>
          <Icon icon="mdi:account-cancel-outline" class="mr-1" /> Close account
        </Button>
      </div>
    </Card>
  {/if}

  <ConfirmDialog
    open={confirmingClose}
    title="Close this account?"
    body={`Close ${account.email}? They will lose access.`}
    confirmLabel="Close account"
    onconfirm={closeAccount}
    oncancel={() => { confirmingClose = false; }}
  />
</AdminLayout>
